import { ArrowLeft, LoaderCircle, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { createQuickPrompt, deleteQuickPrompt, updateQuickPrompt } from "../api";
import type { Notify, QuickPromptContext, QuickPromptRecord } from "../types";

interface QuickPromptsManagerProps {
  prompts: QuickPromptRecord[];
  onChanged(): Promise<void>;
  onClose(): void;
  onNotify: Notify;
}

interface PromptDraft {
  title: string;
  description: string;
  content: string;
  context: QuickPromptContext;
}

const EMPTY_DRAFT: PromptDraft = {
  title: "",
  description: "",
  content: "",
  context: "all",
};

export function QuickPromptsManager({ prompts, onChanged, onClose, onNotify }: QuickPromptsManagerProps) {
  const [editing, setEditing] = useState<QuickPromptRecord | "new" | null>(null);
  const [draft, setDraft] = useState<PromptDraft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function openEditor(prompt: QuickPromptRecord | "new") {
    setEditing(prompt);
    setDraft(prompt === "new" ? EMPTY_DRAFT : {
      title: prompt.title,
      description: prompt.description,
      content: prompt.content,
      context: prompt.context,
    });
    setError("");
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!draft.title.trim() || !draft.content.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const created = editing === "new";
      if (created) await createQuickPrompt(draft);
      else if (editing) await updateQuickPrompt(editing.id, draft);
      await onChanged();
      setEditing(null);
      onNotify(created ? "Prompt rapide créé." : "Prompt rapide mis à jour.", "success");
    } catch (caught) {
      const message = errorMessage(caught);
      setError(message);
      onNotify(message, "error");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!editing || editing === "new" || busy) return;
    if (!window.confirm(`Supprimer le prompt « ${editing.title} » ?`)) return;
    setBusy(true);
    setError("");
    try {
      await deleteQuickPrompt(editing.id);
      await onChanged();
      setEditing(null);
      onNotify("Prompt rapide supprimé.", "success");
    } catch (caught) {
      const message = errorMessage(caught);
      setError(message);
      onNotify(message, "error");
    } finally {
      setBusy(false);
    }
  }

  return createPortal((
    <div className="quick-prompt-manager-layer">
      <button className="quick-prompt-manager-backdrop" type="button" onClick={onClose} aria-label="Fermer" />
      <section className="quick-prompt-manager" role="dialog" aria-modal="true" aria-label="Gérer les prompts rapides">
        <header>
          {editing ? (
            <button className="icon-command" type="button" onClick={() => setEditing(null)} aria-label="Retour à la liste" title="Retour">
              <ArrowLeft aria-hidden="true" />
            </button>
          ) : <span className="quick-prompt-header-spacer" />}
          <div>
            <strong>{editing === "new" ? "Nouveau prompt" : editing ? "Modifier le prompt" : "Prompts rapides"}</strong>
            <span>{editing ? contextLabel(draft.context) : `${prompts.length} prompt${prompts.length > 1 ? "s" : ""}`}</span>
          </div>
          {!editing ? (
            <button className="icon-command" type="button" onClick={() => openEditor("new")} aria-label="Créer un prompt rapide" title="Nouveau prompt">
              <Plus aria-hidden="true" />
            </button>
          ) : null}
          <button className="icon-command" type="button" onClick={onClose} aria-label="Fermer" title="Fermer">
            <X aria-hidden="true" />
          </button>
        </header>

        {editing ? (
          <form className="quick-prompt-editor" onSubmit={save}>
            <div className="quick-prompt-editor-scroll">
              <label className="form-field">
                <span>Nom</span>
                <input
                  value={draft.title}
                  maxLength={80}
                  onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                  autoFocus
                  required
                />
              </label>
              <label className="form-field">
                <span>Description courte</span>
                <input
                  value={draft.description}
                  maxLength={160}
                  onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                />
              </label>
              <label className="form-field">
                <span>Disponible dans</span>
                <select
                  value={draft.context}
                  onChange={(event) => setDraft((current) => ({ ...current, context: event.target.value as QuickPromptContext }))}
                >
                  <option value="all">Toutes les conversations</option>
                  <option value="general">Conversations générales</option>
                  <option value="project">Sessions de projet</option>
                </select>
              </label>
              <label className="form-field quick-prompt-content-field">
                <span>Prompt</span>
                <textarea
                  value={draft.content}
                  maxLength={10_000}
                  onChange={(event) => setDraft((current) => ({ ...current, content: event.target.value }))}
                  required
                />
              </label>
              {error ? <p className="quick-prompt-error" role="alert">{error}</p> : null}
            </div>
            <footer className="quick-prompt-savebar">
              {editing !== "new" ? (
                <button className="text-command danger" type="button" onClick={remove} disabled={busy}>
                  <Trash2 aria-hidden="true" />
                  Supprimer
                </button>
              ) : <span />}
              <button className="text-command primary" type="submit" disabled={busy || !draft.title.trim() || !draft.content.trim()}>
                {busy ? <LoaderCircle className="spin" aria-hidden="true" /> : <Save aria-hidden="true" />}
                {busy ? "Enregistrement" : "Enregistrer"}
              </button>
            </footer>
          </form>
        ) : (
          <div className="quick-prompt-manager-list">
            {prompts.length ? prompts.map((prompt) => (
              <button type="button" key={prompt.id} onClick={() => openEditor(prompt)}>
                <span>
                  <strong>{prompt.title}</strong>
                  <small>{prompt.description || contextLabel(prompt.context)}</small>
                </span>
                <em>{contextLabel(prompt.context)}</em>
                <Pencil aria-hidden="true" />
              </button>
            )) : (
              <div className="quick-prompt-manager-empty">
                <strong>Aucun prompt rapide</strong>
                <button className="text-command primary" type="button" onClick={() => openEditor("new")}>
                  <Plus aria-hidden="true" />
                  Créer
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  ), document.body);
}

function contextLabel(context: QuickPromptContext): string {
  if (context === "project") return "Projets";
  if (context === "general") return "Général";
  return "Partout";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Action impossible.";
}
